//Library Imports
import React, { useState, useEffect } from "react";
import { Modal, Col, Row, Select, Radio } from "antd";
import { useDispatch, useSelector } from "react-redux";
//Local Imports
import { RootState } from "store";
import SmallButton from "../../../Content/Shared/SmallButton";
import Input from "../../../../modules/Common/Components/Input";
import SelectWithIcon from "../../../../modules/Campaigns/Shared/SelectWithIcon";
import { ReactComponent as Cross } from "../../../../assets/img/icons/cross.svg";
import Plus from "../../../../assets/img/icons/plus-5.svg";
// actions Imports
import { addNode, clearDropArea, addActionModal, editNode } from "../../actions";
import { ConditionEmails, ConditionEnum, ControlNameEnum, IControls, IShareProfileTemplate } from "../../interfaces";
import { basedOnValues, findDropAreaData, getConditionEmails, getConditionNodes } from "../../utils";
//Style Imports
import styled from "styled-components";
import ConditionSelection from "../ConditionSelection";

type IWaitModalProps = {
  id: string;
  enableSaveButton: (value: boolean) => void;
  handleSaveButton: (conditions: object[]) => void;
  thresholdTime: string;
  waitRadio: number;
  setWaitRadio: (val: number) => void;
  setThresholdType: (val: { value: TimeUnits, label: TimeUnits }) => void;
  setThresholdTime: (val: number) => void;
  activeKey: string;
};

export enum TimeUnits {
  DAYS = "Days",
  HOURS = "Hours",
  MINUTES = "Minutes",
}

const TimeSerializer = {
  d: TimeUnits.DAYS,
  h: TimeUnits.HOURS,
  m: TimeUnits.MINUTES,
};

type IConditionControls = {
  id: string;
  value: ConditionEnum.HAS_CLICKED | ConditionEnum.HAS_OPENED | ConditionEnum.CURRENT_DAY | ConditionEnum.CURRENT_TIME;
  label: ConditionEnum.HAS_CLICKED | ConditionEnum.HAS_OPENED | ConditionEnum.CURRENT_DAY | ConditionEnum.CURRENT_TIME;
};
const { Option } = Select;

const WaitModal = (props: IWaitModalProps) => {
  // Props
  const { id, enableSaveButton, handleSaveButton, thresholdTime, waitRadio, setWaitRadio, setThresholdType, setThresholdTime, activeKey } = props;
  // Redux States
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[id]?.detail);
  const modal = useSelector((state: RootState) => state.automation.modals.addAction);
  const dropData = useSelector((state: RootState) => state.automation.workflow.dropArea);
  const shareProfileTemplates: IShareProfileTemplate[] = useSelector(
    (state: RootState) => state.automation.emails.shareProfileTemplates
  );
  const controls: IControls[] = useSelector((state: RootState) => state.automation.controls);

  // Local Variables
  const dispatch = useDispatch();
  const minutes = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  const defaultMinutes = 15;
  const initialCondition = [
    {
      emailId: 0,
      emailType: "",
      emailName: "",
      link: "",
      linkType: "",
      sp_uuid: "",
      controlId: 3,
      controlName: ConditionEnum.HAS_OPENED,
      timezone: "",
      verb: "Is",
      day: "",
      time: "",
    },
  ];
  let waitConditionControls: IConditionControls[] = controls
    .find((control: IControls) => control.name === ControlNameEnum.IF_ELSE)
    ?.conditions?.map((condition) => {
      return {
        id: condition.id,
        value: condition.name,
        label: condition.name,
      };
    }) || [{ id: "", value: "", label: "" }];
  const emails: ConditionEmails[] = getConditionEmails(dropData.id || id, shareProfileTemplates);
  const nodes: any = getConditionNodes(dropData.id || id);
  const isWaitModalOpen = modal.visible === true && modal.tab === ControlNameEnum.WAIT;
  let dependencies = itemData && isWaitModalOpen;

  // local states
  const [time, setTime] = useState(1);
  const [type, setType] = useState({ value: TimeUnits.DAYS, label: TimeUnits.DAYS });
  const [basedOn, setBasedOn] = useState({ value: basedOnValues.businessDays.value, label: basedOnValues.businessDays.label });
  const [conditions, setConditions] = useState(initialCondition);
  const [conditionOperators, setConditionOperators] = useState<any>([]);


  // UseEffects
  useEffect(() => {
    // Set the data of edit Node
    if (isWaitModalOpen && itemData) {
      let data: ["", string, "d" | "m" | "h"] = itemData?.delayTime?.split(/(\d+)/);
      // White screen appears in a case where data is undefined
      if (data !== undefined) {
        let timeType = TimeSerializer[data[2]?.trim()];
        if (waitRadio === 1 && (!itemData?.radioValue || itemData?.radioValue === 1)) {
          // Normal Delay
          if (data?.length > 1) {
            setTime(parseInt(data[1]));
            setType({ value: timeType, label: timeType });
            if (itemData.basedOn)
              setBasedOn({ value: itemData.basedOn, label: Object.values(basedOnValues).find((base) => base.value === itemData.basedOn)!.label })
          }
        } else {
          // Delay Conditions
          if (data?.length > 1) {
            setThresholdTime(parseInt(data[1]));
            setThresholdType({ value: timeType, label: timeType });
          }
          setWaitRadio(itemData?.radioValue ?? 1);
          updateCurrentStates();
        }
      }
    }
  }, [dependencies, id]);

  useEffect(() => {
    // Save button enable disable handling on switching conditions tabs
    if (activeKey === ControlNameEnum.WAIT && waitRadio === 2)
      handleSaveButton(conditions);
  }, [activeKey]);

  useEffect(() => {
    updateCurrentStates();
  }, [emails.length]);

  useEffect(() => {
    if (modal.visible === false) {
      enableSaveButton(false);
      resetUpdate();
    }
  }, [modal]);

  useEffect(() => {
    if (!modal.visible) {
      setBasedOn({ value: basedOnValues.businessDays.value, label: basedOnValues.businessDays.label });
      setTime(1);
      setType({ value: TimeUnits.DAYS, label: TimeUnits.DAYS });
      setThresholdTime(14);
      setThresholdType({ value: TimeUnits.DAYS, label: TimeUnits.DAYS });
      setWaitRadio(1);
    }
  }, [modal.visible])

  // Component Logic
  const onAddCondition = () => {
    let updatedConditions = [
      ...conditions,
      {
        emailId: 0,
        emailType: "",
        emailName: "",
        link: "",
        linkType: "",
        sp_uuid: "",
        controlId: 3,
        controlName: ConditionEnum.HAS_OPENED,
        timezone: "",
        verb: "Is",
        day: "",
        time: "",
      },
    ];
    setConditions([...updatedConditions]);
    if (conditionOperators?.includes("OR")) {
      setConditionOperators((state: any) => [...state, "OR"]);
    } else {
      setConditionOperators((state: any) => [...state, "AND"]);
    }
    handleSaveButton(updatedConditions);
  };

  const removeCondition = (index: number) => {
    let filteredConditions = [...conditions].filter((con, idx) => idx !== index);
    setConditions(filteredConditions);
    let updatedConditionOperators = [...conditionOperators];
    updatedConditionOperators.pop();
    setConditionOperators([...updatedConditionOperators]);

    handleSaveButton(filteredConditions);
  };

  const updateNode = (condition: any, index: number) => {
    let updatedConditions = [...conditions];
    if (updatedConditions[index]) {
      updatedConditions[index] = condition;
      setConditions([...updatedConditions]);
    }
    handleSaveButton(updatedConditions);
  };

  const resetUpdate = () => {
    setConditions(initialCondition);
    setConditionOperators([]);
  };

  const updateCurrentStates = () => {
    if (id && itemData) {
      if (itemData.conditions) {
        let conditionsArray = [...itemData.conditions];
        let updated = conditionsArray.map((condition) => {
          let conditionObject = { ...condition };
          let emailId = conditionObject?.emailId;
          let indexOfEmail = emails.findIndex(
            (email) =>
              email?.emailId?.toString() === emailId?.toString() ||
              email?.sp_uuid?.toString() === emailId?.toString()
          );
          if (indexOfEmail < 0) {
            conditionObject.emailId = 0;
            conditionObject.emailName = "";
            conditionObject.emailType = "";
            conditionObject.sp_uuid = "";
            conditionObject.link = "";
            conditionObject.linkType = "";
          }
          return conditionObject;
        });
        setConditions([...updated]);
        handleSaveButton(itemData.conditions);
        if (itemData?.conditionOperators.length > 0) {
          setConditionOperators([...itemData?.conditionOperators]);
        }
      }
    } else {
      if (conditions.length > 1) {
        setConditions(initialCondition);
        setConditionOperators([]);
      }
    }
  };

  const closeModal = () => {
    // dispatch(waitModal(false));
    dispatch(addActionModal(false));
    dispatch(clearDropArea());
  };

  const delayTypeChangeHandler = (valueObj: any) => {
    setType(valueObj);
    if (valueObj.value === TimeUnits.MINUTES) {
      setTime(defaultMinutes);
    } else if (valueObj.value === TimeUnits.HOURS) {
      setTime(1);
    } else if (valueObj.value === TimeUnits.DAYS) {
      setTime(1);
    }
  };

  const waitRadioChangeHandler = (value: number) => {
    setWaitRadio(value);
    if (value === 2)
      handleSaveButton(conditions);
    else
      enableSaveButton(true);
  };

  const submitHandler = (event: any) => {
    event.preventDefault();
    let data = `${time}${type?.value?.charAt(0)?.toLowerCase()}`;
    let title = ` ${time} ${type.value}`;
    if (id) {
      // edit Node
      dispatch(
        editNode(id, {
          ...itemData,
          delayTime: waitRadio === 1 ? data : thresholdTime,
          nodeTitle: title,
          ...(waitRadio === 1 && { conditionOperators: [], conditions: initialCondition, radioValue: waitRadio, basedOn: basedOn.value }),
          ...(waitRadio === 2 && { conditionOperators, conditions, radioValue: waitRadio, error: false }),
        })
      );
    } else {
      // add node
      let obj = findDropAreaData(ControlNameEnum.WAIT);
      dispatch(
        addNode({
          ...obj,
          delayTime: waitRadio === 1 ? data : thresholdTime,
          nodeTitle: title,
          ...(waitRadio === 1 && { basedOn: basedOn.value }),
          ...(waitRadio === 2 && { conditionOperators, conditions, radioValue: waitRadio }),
        })
      );
    }
    closeModal();
  };

  return (
    <Wrapper>
      <Radio.Group
        onChange={(e) => waitRadioChangeHandler(e.target.value)}
        name="waitType"
        className={`d-flex flex-column radio-group`}
        value={waitRadio}
      >
        <Radio className={"mb-2 radio-btn radio-btn-position"} value={1}>
          Wait for a specified period of time
        </Radio>
        <Radio className={"mb-2 radio-btn"} value={2}>
          Wait until specific conditions are met
        </Radio>
      </Radio.Group>
      {waitRadio === 1 ?
        <form id={`${ControlNameEnum.WAIT}`} onSubmit={submitHandler}>
          <div className="wait-wrapper">
            <Row gutter={[32, 0]}>
              <Col span={16} className="delay-input-styles left-side">
                {type.value === TimeUnits.MINUTES ? (
                  <Select
                    onSelect={(value: number) => {
                      setTime(value);
                    }}
                    defaultValue={defaultMinutes}
                    style={{ width: "100%" }}
                    value={time}
                  >
                    {minutes.map((time: number) => (
                      <Option key={time} value={time}>
                        {time}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    type="number"
                    placeholder="Wait for"
                    min="1"
                    step="1"
                    max="9999"
                    value={time}
                    onChange={(e: any) => {
                      const limit = 4;
                      setTime(Math.trunc(e.target.value.slice(0, limit)))
                    }}
                  />
                )}
              </Col>
              <Col span={8}>
                <SelectWithIcon
                  defaultValue={{ value: "Days", label: "Days" }}
                  value={type}
                  onChange={delayTypeChangeHandler}
                  options={[
                    { value: TimeUnits.DAYS, label: TimeUnits.DAYS },
                    { value: TimeUnits.HOURS, label: TimeUnits.HOURS },
                    { value: TimeUnits.MINUTES, label: TimeUnits.MINUTES },
                  ]}
                  menuWidth={120}
                  className="wait-dropdown"
                />
              </Col>
            </Row>
            <div className="label-input">Based on</div>
            <Row gutter={[32, 0]}>
              <Col span={16} className="left-side">
                <SelectWithIcon
                  defaultValue={{ value: basedOnValues.businessDays.value, label: basedOnValues.businessDays.label }}
                  value={basedOn}
                  onChange={(base) => setBasedOn(base)}
                  options={[
                    { value: basedOnValues.allDays.value, label: basedOnValues.allDays.label },
                    { value: basedOnValues.businessDays.value, label: basedOnValues.businessDays.label },
                  ]}
                  menuWidth={120}
                  className="wait-dropdown"
                />
              </Col>
            </Row>
          </div>
        </form>
        :
        <form id={`${ControlNameEnum.WAIT}`} onSubmit={submitHandler}>
          <div className="condition-wrapper">
            <div className="label-condition">Select a condition</div>
            {conditions.map((condition, index) => (
              <ConditionSelection
                id={id}
                key={index}
                index={index}
                emails={emails}
                nodes={nodes}
                condition={condition}
                conditions={conditions}
                conditionOperators={conditionOperators}
                setConditions={setConditions}
                setConditionOperators={setConditionOperators}
                enableSaveButton={enableSaveButton}
                isAdditional={index !== 0}
                removeCondition={removeCondition}
                updateNode={updateNode}
                conditionControls={waitConditionControls}
                waitRadioValue={waitRadio}
              />
            ))}
            <div className="add-condition-wrapper">
              <div className="add-condition-button" onClick={onAddCondition}>
                <img src={Plus} alt="" /> Add another condition
              </div>
            </div>
          </div>
        </form>
      }
    </Wrapper>
  );
};

export default WaitModal;

const Wrapper = styled.div`
  .radio-group{
    margin-top: 16px;
    margin-bottom: 16px;
    .ant-radio-wrapper{
      .ant-radio{
        top:0;
      }
    }
    :nth-child(2){
      .ant-radio{
        .ant-radio-inner{
          ::after{
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              top: 3.5px;
            }
          }
        }
      }
    }
  }
  .radio-btn{
    width: fit-content;
    position: initial;
    justify-content: center;
    align-items: center;
    display: flex;
    margin-left: 3px;
    .ant-radio{
      .radio-btn-position .ant-radio-inner{
        ::after{
          width: 10px;
          height: 10px;
          top: 3.5px;
          left: 3.5px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            height: 8px;
            width: 8px;
            margin-left: 2px;
          }
        }
        display: flex;
        align-items: center;
        justify-content: center;
        position: initial;
        width: 20px;
        height: 20px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          height: 16px;
          width: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: initial;
          margin-left: 2px;
        }
      }
    }
    .ant-radio ~ span{
      font-family: "Roboto";
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: -0.3999999761581421px;
      color: #5E7079;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 14px;
      }
    }
  }
  .condition-wrapper {
    height: 453px;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: #001760 #efefef;
    width: calc(100% + 10px) !important;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 370px;
    }
    margin-left: -10px;
    padding-left: 10px;
    padding-right: 30px;
    padding-bottom: 50px;
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      border-radius: 5px;
    }
    /* Track */
    &::-webkit-scrollbar-track {
      background: #fff;
      margin-top: 15px;
      margin-bottom: 15px;
    }
    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #001760;
    }
    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #01218a;
    }
    div[class$="-control"] {
      @media all and (max-width: 922px) and (min-width: 860px) {
        min-width: 280px;
        max-width: calc(100% - 15px);
      }
    }
    .ant-row{
      .time-dropdown{
        div{
          div{
            .css-1uxsi1k-menu{
              > div {
                  max-height: 148px !important;
                  border-radius: inherit !important;
                }
                div {
                  border-radius: inherit !important;
                }
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  max-height: 157px !important;
                }
            }
          }
        }
      }
    }
    .ant-select-item-group {
      font-family: "Roboto";
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 16px;
      letter-spacing: -0.4px;
      color: #424b50;
      .ant-select-item-option-content {
        color: #979797;
      }
    }
    .ant-select-item-option-content,
    .ant-select-selection-item {
      color: #979797;
      span {
        align-items: center;
        display: flex;
        color: #979797;
        font-family: "Roboto";
        font-size: 12px;
        text-decoration: underline;
        text-decoration-color: #979797;
        width: 100%;
        .ant-typography-ellipsis-single-line {
          margin-bottom: 0;
          text-decoration: underline;
          color: #979797;
          text-decoration-color: #979797;
        }
        .read-more {
          font-size: 12px;
          color: #27ce0a;
          margin-top: 0;
          margin-bottom: 0;
          text-decoration: underline;
        }
      }
      :hover {
        color: #979797;
      }
    }
    .ant-select-selection-item {
      max-width: 250px !important;
      .ant-typography-ellipsis-single-line {
        width: 90%;
      }
    }
    .ant-select-item-option-content:hover {
      color: #979797;
    }
    .any-link {
      .ant-select-item-option-content {
        font-size: 16px;
        color: #27ce0a;
        text-decoration: underline;
        text-decoration-color: #27ce0a;
      }
    }
    .text-underlined {
      text-decoration: underline;
      text-decoration-color: #979797;
      &.ant-select-item-option-content {
        color: #979797;
      }
      /* :hover {
        text-decoration-color: #27ce0a;
      } */
    }
    .ant-select-item-option-grouped {
      padding-left: 12px;
    }
    .removeBtn {
      background: #ffe9e9;
      border-radius: 5px;
      font-family: "Roboto";
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 14px;
      letter-spacing: -0.35px;
      color: #d22121;
      padding: 4px 11px;
      margin-left: 16px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 11px;
      }
      :hover {
        cursor: pointer;
      }
    }
  }
  .condition-wrapper .ant-radio-button-wrapper:not(.ant-radio-button-wrapper-checked) {
    color: #27ce0a;
  }
  .condition-wrapper .ant-radio-button-wrapper:first-child {
    border-radius: 6px 0 0 6px;
    font-family: "Roboto";
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: -0.35px;
  }
  .condition-wrapper .ant-radio-button-wrapper:last-child {
    border-radius: 0 6px 6px 0;
    font-family: "Roboto";
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: -0.35px;
  }
  .label-input {
    margin: 16px 0px 8px 0px;
    color: #5e7079;
    font-size: 16px;
    font-weight: 500;
    font-family: "Roboto", sans-serif;
  }
  .wait-wrapper {
    label {
      margin: 0 !important;
    }
    .ant-row{
      .delay-input-styles{
        padding-right: 16px !important;
        div > input {
          color: #7E7E7E;
          font-family: "Roboto";
          font-size: 16px;
          font-style: normal;
          font-weight: 500;
          line-height: 20px;
          letter-spacing: -0.4px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
        }
      }
    }
    div[class$="-control"] {
      min-width: 100%;
      padding-left: 2px;
      height: 44px;
      border-color: #dadada;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: auto;
      }
    }
    input {
      border-color: #dadada;
      padding-right: 15px;
    }
    .left-side {
      .ant-select-selector {
        height: 44px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          height: 32px;
        }
      }
    }
  }
  .limit-text{
    font-family: "Roboto";
    font-size: 12px;
    margin-top: 3px;
    color:  #A0A0A0;
    font-style: italic;
  }
  .note-text{
    color: #7E7E7E;
    font-weight: bold;
  }
`;
